import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';
import classnames from 'classnames';

import LoadingWrapper from 'dpl/components/LoadingWrapper';
import PaymentMethod from 'dpl/buyer_account/components/PaymentMethod';
import useResourceMutation from 'dpl/shared/fetching/hooks/useResourceMutation';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import useToastNotifications from 'dpl/shared/hooks/useToastNotifications';
import { GDButtonV1 } from 'dpl/common/core/GDButton/GDButtonV1';
import {
  chargeSavedPaymentMethodMutation,
  defaultPaymentMethodV1QueryDefintion
} from 'dpl/buyer_account/utils/resourceDefinitions';
import { stripePromise } from 'dpl/util/stripePromise';
import isFeatureEnabled from 'dpl/util/isFeatureEnabled';
import { FEATURES } from 'dpl/shared/constants/features';

import StripeChargeModalFooter from './StripeChargeModalFooter';
import { SUBMIT_PAYMENT_LABEL } from './constants';

const IS_REL_PAYMENT_REQUEST_STEPS_V2_ENABLED = isFeatureEnabled(
  FEATURES.REL_PAYMENT_REQUEST_STEPS_V2
);

function PrivateFormComponent({
  children,
  className,
  externalId,
  id,
  onChange, // Fires onChange and onReady for StripePaymentElement if no default payment is set
  onSubmit,
  isFetching
}) {
  const elements = useElements();
  const stripe = useStripe();
  const { pushToastNotification } = useToastNotifications();

  const [isDefaultPaymentSelected, setIsDefaultPaymentSelected] =
    useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const successPageUrl = new URL(
    `/pay/${externalId}/success`,
    window.location.origin
  ).href;

  const { data, isFetching: isDefaultPaymentMethodFetching } = useResourceQuery(
    defaultPaymentMethodV1QueryDefintion()
  );
  const defaultPaymentMethod = data?.data?.default_payment_method;

  // * When the form is submitting, we could not find a way to make the form elements not clickable. // * So to prevent the customer from changing stuff part way through submitting, we just don't send // * `onChange` events. On submit fail, this can cause a de-sync between the UI and the actual payment // * request record.
  function handleChange(stripeData) {
    if (!isSubmitting) {
      onChange(stripeData);
    }
  }

  useEffect(() => {
    setIsDefaultPaymentSelected(!!defaultPaymentMethod);
  }, [defaultPaymentMethod]);

  useEffect(() => {
    if (isDefaultPaymentSelected) {
      handleChange({ value: { type: defaultPaymentMethod.type } });
    }
  }, [isDefaultPaymentSelected]);

  const { mutateAsync: chargeSavedPaymentMethodAsync } = useResourceMutation(
    chargeSavedPaymentMethodMutation(id)
  );

  async function handleSubmit(e) {
    try {
      e.preventDefault();

      setIsSubmitting(true);

      await onSubmit?.();

      if (isDefaultPaymentSelected) {
        const {
          data: {
            payment_intent: { status, client_secret: clientSecret }
          }
        } = await chargeSavedPaymentMethodAsync();

        if (status === 'requires_action') {
          const { error } = await stripe.confirmCardPayment(clientSecret, {
            return_url: successPageUrl
          });
          if (error) {
            pushToastNotification({ message: error.message, type: 'error' });
          } else {
            window.location.assign(successPageUrl);
          }
        } else {
          window.location.assign(successPageUrl);
        }
      } else {
        const { error } = await stripe.confirmPayment({
          confirmParams: {
            return_url: successPageUrl
          },
          elements
        });

        if (error && error.type !== 'validation_error') {
          pushToastNotification({ message: error.message, type: 'error' });
        }
      }
    } catch {
      pushToastNotification({
        type: 'error',
        message: 'There was a problem charging your saved payment method.'
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <form
      className={classnames('flex flex-column', className)}
      style={{ gap: '1rem' }}
    >
      <LoadingWrapper isLoading={isDefaultPaymentMethodFetching}>
        {defaultPaymentMethod && (
          <section>
            <PaymentMethod
              className={classnames('bw1', {
                'b--purple': isDefaultPaymentSelected
              })}
              style={{
                outline: isDefaultPaymentSelected ? '2px solid #CACDF6' : ''
              }}
              paymentMethod={defaultPaymentMethod}
              shouldShowActiveLabel={false}
              onClick={() => {
                setIsDefaultPaymentSelected(true);
              }}
            />
            <GDButtonV1
              className="mt1 bn f2 o-60"
              onClick={() => {
                setIsDefaultPaymentSelected(!isDefaultPaymentSelected);
              }}
              outlined
              style={{ paddingLeft: 0 }}
              variant="secondary"
            >
              {isDefaultPaymentSelected
                ? '+ Add new payment method'
                : '- Use default payment method'}
            </GDButtonV1>
          </section>
        )}
        {!isDefaultPaymentSelected && (
          <section>
            <h2 className="lh-title f2 mb4 font-sans">Select payment method</h2>
            <PaymentElement
              options={{ readOnly: isFetching || isSubmitting }}
              onChange={handleChange}
            />
          </section>
        )}
        {children}
        <div>
          <GDButtonV1
            disabled={isSubmitting || isFetching}
            type="submit"
            onClick={handleSubmit}
            className="PayNowButton w-100"
          >
            {SUBMIT_PAYMENT_LABEL}
          </GDButtonV1>
          <StripeChargeModalFooter />
        </div>
      </LoadingWrapper>
    </form>
  );
}

PrivateFormComponent.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  isFetching: PropTypes.bool.isRequired,
  externalId: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  onSubmit: PropTypes.func
};

PrivateFormComponent.defaultProps = {
  children: null,
  className: '',
  onChange: null,
  onSubmit: null
};

export function CompletePaymentRequestFormV1({ clientSecret, ...args }) {
  const options = { clientSecret };

  if (IS_REL_PAYMENT_REQUEST_STEPS_V2_ENABLED) {
    options.appearance = {
      variables: { spacingUnit: '6px', borderRadius: '8px' }
    };
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <PrivateFormComponent {...args} />
    </Elements>
  );
}

CompletePaymentRequestFormV1.propTypes = {
  ...PrivateFormComponent.propTypes,
  clientSecret: PropTypes.string.isRequired
};

CompletePaymentRequestFormV1.defaultProps = {
  ...PrivateFormComponent.defaultProps
};
